/**
 * Texto do jogo em bitmap font 1-bit.
 *
 * Todo texto da UI passa por aqui. `Phaser.GameObjects.Text` está PROIBIDO no
 * cliente: ele delega a rasterização ao motor de fonte do navegador, que
 * antialiasa sempre — em 7–10px, 100% dos pixels do glifo saíam cinza, e a
 * ampliação NEAREST do canvas transformava esse cinza em blocos. Letras viravam
 * outras ("JOGADOR" lia "JOBADOR"). Os atlas de `assets/fonts/pixelfont_*.png`
 * são limiarizados offline por `npm run font` e não têm um único pixel
 * intermediário, então a ampliação preserva a forma da letra.
 *
 * Consequência prática: só existem os tamanhos de `SIZES`, e sempre em escala
 * 1. Pedir 11px devolveria meio pixel de fonte e traria o borrão de volta.
 */

import Phaser from 'phaser'

/** Tamanhos com atlas gerado. Manter em sincronia com `scripts/build-bitmap-font.mjs`. */
export const SIZES = [8, 10, 12, 16, 24, 32, 48] as const

export type PixelSize = (typeof SIZES)[number]

const keyOf = (size: PixelSize): string => `px${size}`

/**
 * Enfileira os atlas. Idempotente: cada cena chama no seu `preload()` e quem
 * já está no cache é pulado (o loader do Phaser reclama de chave duplicada).
 */
export function preloadPixelFonts(scene: Phaser.Scene): void {
  for (const size of SIZES) {
    const key = keyOf(size)
    if (scene.cache.bitmapFont.exists(key)) continue
    scene.load.bitmapFont(key, `assets/fonts/pixelfont_${size}.png`, `assets/fonts/pixelfont_${size}.xml`)
  }
}

export interface PixelTextOptions {
  /** Cor em CSS (`#rrggbb`) — aplicada como tint sobre o atlas branco. */
  color?: string
  origin?: [number, number]
  align?: 'left' | 'center' | 'right'
  letterSpacing?: number
  depth?: number
}

const toTint = (css: string): number => Phaser.Display.Color.HexStringToColor(css).color

/** Cria um texto nítido. O tamanho DEVE ser um dos de `SIZES`. */
export function pixelText(
  scene: Phaser.Scene,
  x: number,
  y: number,
  size: PixelSize,
  text: string,
  options: PixelTextOptions = {},
): Phaser.GameObjects.BitmapText {
  // Sem o 5º argumento o BitmapText usa o tamanho nativo do atlas: escala 1,
  // um pixel de fonte por pixel de tela. Passar tamanho aqui reintroduz
  // escala fracionária e desfaz todo o trabalho do gerador.
  const object = scene.add.bitmapText(x, y, keyOf(size), text)

  if (options.color !== undefined) object.setTint(toTint(options.color))
  if (options.align === 'center') object.setCenterAlign()
  else if (options.align === 'right') object.setRightAlign()
  if (options.letterSpacing !== undefined) object.setLetterSpacing(options.letterSpacing)
  // A origem depende dos bounds, que só existem depois do texto — por isso
  // vem DEPOIS do align, e é reaplicada em `setPixelText` quando o texto muda.
  if (options.origin !== undefined) object.setOrigin(options.origin[0], options.origin[1])
  if (options.depth !== undefined) object.setDepth(options.depth)

  return object
}

/**
 * Troca o texto preservando a origem. `BitmapText.setText` recalcula os bounds
 * mas NÃO reposiciona a origem: um texto centralizado que muda de largura fica
 * torto se a origem não for reaplicada (visível no placar e no centro da tela).
 */
export function setPixelText(object: Phaser.GameObjects.BitmapText, text: string): void {
  const originX = object.originX
  const originY = object.originY
  object.setText(text)
  object.setOrigin(originX, originY)
}

/**
 * Troca o TAMANHO de um texto já criado — trocando de atlas, não escalando.
 *
 * `setFontSize` existe no `BitmapText`, mas escala a textura: pedir 48 num
 * atlas de 14 dá fator 3,43 e devolve exatamente o borrão que os atlas
 * existem para evitar. Aqui a família muda junto, e o tamanho vai explícito
 * porque `setFont` sem tamanho preserva o do atlas anterior.
 */
export function setPixelSize(object: Phaser.GameObjects.BitmapText, size: PixelSize): void {
  if (object.font === keyOf(size)) return
  const originX = object.originX
  const originY = object.originY
  object.setFont(keyOf(size), size)
  object.setOrigin(originX, originY)
}

/** Contorno em cruz: leve o bastante para texto miúdo não empastar. */
const OUTLINE_CROSS = [
  [0, -1],
  [-1, 0],
  [1, 0],
  [0, 1],
] as const

/** Contorno fechado, para texto grande — veda também as diagonais. */
const OUTLINE_FULL = [
  [-1, -1],
  [0, -1],
  [1, -1],
  [-1, 0],
  [1, 0],
  [-1, 1],
  [0, 1],
  [1, 1],
] as const

export interface OutlinedOptions {
  color: string
  outline: string
  align?: 'left' | 'center' | 'right'
  depth?: number
  /** `full` veda as diagonais; `cross` é o padrão, melhor em corpo pequeno. */
  weight?: 'cross' | 'full'
}

/**
 * Texto com contorno de 1px.
 *
 * `BitmapText` não tem `stroke` — isso era do rasterizador do `Text` — e
 * `setDropShadow` resolveria só metade: é WEBGL-ONLY, então sumiria calado num
 * aparelho que caísse para Canvas, justo onde a legibilidade já é pior. Cópias
 * deslocadas funcionam em qualquer renderizador e dão borda de pixel cheio.
 *
 * É o que mantém texto miúdo legível sobre o cenário que rola no menu: ali o
 * fundo muda de verde para azul embaixo da mesma palavra, e nenhuma cor de
 * texto sozinha vence os dois.
 */
export class OutlinedText {
  readonly container: Phaser.GameObjects.Container
  private readonly outlines: Phaser.GameObjects.BitmapText[]
  private readonly main: Phaser.GameObjects.BitmapText

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    size: PixelSize,
    text: string,
    options: OutlinedOptions,
  ) {
    const offsets = options.weight === 'full' ? OUTLINE_FULL : OUTLINE_CROSS
    // `align` entra condicionalmente: com exactOptionalPropertyTypes, passar
    // a chave valendo undefined não é o mesmo que omiti-la.
    const shared: Omit<PixelTextOptions, 'color'> = {
      origin: [0.5, 0.5],
      ...(options.align === undefined ? {} : { align: options.align }),
    }
    this.outlines = offsets.map(([dx, dy]) =>
      pixelText(scene, dx, dy, size, text, { ...shared, color: options.outline }),
    )
    this.main = pixelText(scene, 0, 0, size, text, { ...shared, color: options.color })
    this.container = scene.add.container(x, y, [...this.outlines, this.main])
    if (options.depth !== undefined) this.container.setDepth(options.depth)
  }

  /** Troca o texto em todas as camadas de uma vez. */
  setText(text: string): void {
    for (const layer of this.outlines) setPixelText(layer, text)
    setPixelText(this.main, text)
  }
}

/** Título: mesmo contorno, fechado. */
export function pixelTitle(
  scene: Phaser.Scene,
  x: number,
  y: number,
  size: PixelSize,
  text: string,
  color: string,
  outline: string,
): OutlinedText {
  return new OutlinedText(scene, x, y, size, text, { color, outline, weight: 'full' })
}
